import React, { FunctionComponent, useCallback, useMemo } from "react";
import { FormControl, MenuItem, TextField, useTheme } from "@mui/material";
import { tokens } from "../layout/theme";
import { useField, useFormikContext } from "formik";
import { InputAdornment, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

/**
 * Interface defining the props for the SelectField component.
 * @property {string} name - The name of the form field, used for formik integration
 * @property {string} label - The label text to display above the select field
 * @property {string} [type] - The type of select field
 * @property {boolean} [required=false] - Whether the field is required
 * @property {boolean} [disabled=false] - Whether the field is disabled
 * @property {boolean} [showClearIcon=false] - Whether to show a clear selection icon
 * @property {any} [value] - The current value of the field
 * @property {any[]} [options] - Array of options to display in the select field
 * @property {string} [keyItem] - The key to use for displaying option text
 * @property {string} [extraKey] - Additional key to display extra information for each option
 * @property {string} [errorMessage="Keine Optionen verfügbar"] - Message to display when no options are available
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - Callback for selection changes
 * @property {boolean} [multiple=false] - Whether multiple selections are allowed
 */
interface SelectFieldProps {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  showClearIcon?: boolean;
  value?: any;
  options?: any;
  keyItem?: any;
  extraKey?: any;
  errorMessage?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  multiple?: boolean;
  SelectProps?: object;
}

/**
 * A reusable select field component that integrates with Formik and Material-UI.
 * 
 * This component provides a styled select input with support for:
 * - Single and multiple selection modes
 * - Custom option rendering with extra information
 * - Clear selection functionality
 * - Form validation and error handling
 * - Theme-based styling
 * 
 * @component
 * @param {SelectFieldProps} props - The props passed to the component
 * @param {string} props.name - The name of the form field
 * @param {string} props.label - The label text
 * @param {string} [props.type] - The select field type
 * @param {any[]} [props.options] - Array of selectable options
 * @param {string} [props.keyItem] - Key for option display text
 * @param {string} [props.extraKey] - Key for additional option info
 * @param {boolean} [props.required=false] - Whether the field is required
 * @param {boolean} [props.disabled=false] - Whether the field is disabled
 * @param {boolean} [props.showClearIcon=false] - Show clear selection icon
 * @param {string} [props.errorMessage="Keine Optionen verfügbar"] - No options message
 * @param {boolean} [props.multiple=false] - Enable multiple selection
 * 
 * @example
 * ```tsx
 * <SelectField
 *   name="country"
 *   label="Select Country"
 *   options={countries}
 *   keyItem="name"
 *   extraKey="code"
 *   required
 *   showClearIcon
 * />
 * ```
 * 
 * @remarks
 * - Integrates with Formik for form state management
 * - Uses Material-UI components for styling
 * - Supports single and multiple selection modes
 * - Provides clear selection functionality
 * - Handles empty options gracefully
 * - Supports additional information display per option
 * - Maintains consistent styling with the application theme
 * 
 * @returns {JSX.Element} A React component that renders a select field
 */
const SelectField: FunctionComponent<SelectFieldProps> = ({
  name,
  type,
  value,
  options,
  keyItem,
  extraKey,
  required = false,
  disabled = false,
  showClearIcon = false,
  errorMessage = "Keine Optionen verfügbar", // Default message when no options are provided
  onChange,
  multiple = false,
  SelectProps,
  ...props
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { setFieldValue } = useFormikContext();
  const [field, meta] = useField(name);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (multiple) {
        const values = Array.isArray(e.target.value)
          ? e.target.value
          : [e.target.value];
        setFieldValue(name, values);
      } else {
        setFieldValue(name, e.target.value);
      }
      if (onChange) {
        onChange(e);
      }
    },
    [name, setFieldValue, onChange, multiple]
  );

  const configSelect = {
    ...field,
    ...props,
    select: true,
    value: value,
    required: required,
    onChange: handleChange,
    error: !!meta.touched && !!meta.error,
    helperText: meta.touched && meta.error ? meta.error : undefined,
    disabled: disabled,
    multiple: multiple,
    SelectProps: SelectProps,
  };

  // Render "No options available" if the options array is empty or undefined
  const mappedOptions = useMemo(() => {
    if (!Array.isArray(options) || options.length === 0) {
      return <MenuItem disabled>{errorMessage}</MenuItem>;
    }

    const clearSelection = (
      <MenuItem key="clear-selection" value="">
        <em style={{ visibility: "hidden" }}>&nbsp;</em>
      </MenuItem>
    );

    const optionItems = options.map((item: any) => (
      <MenuItem
        key={item.id}
        value={item.id !== undefined ? item.id : item[keyItem]}
      >
        {item[keyItem]}
        {extraKey && (
          <>
            <span style={{ marginLeft: "18px" }}></span>
            <i>
              <small>({item[extraKey]})</small>
            </i>
          </>
        )}
      </MenuItem>
    ));

    return [clearSelection, ...optionItems];
  }, [options, keyItem, extraKey, errorMessage]);

  return (
    <FormControl fullWidth variant="outlined">
      <TextField
        variant="outlined"
        fullWidth
        {...configSelect}
        FormHelperTextProps={{ style: { minHeight: "20px" } }}
        sx={{
          borderRadius: 2,
          "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.blueAccent[600],
          },
          "& .MuiFormLabel-root.Mui-focused": {
            color:
              theme.palette.mode === "dark" ? "white" : colors.primary[300],
          },
          "& .MuiFormHelperText-root.Mui-error": {
            marginLeft: 0,
          },
        }}
        InputProps={{
          endAdornment: (
            <>
              {showClearIcon && field.value && (
                <>
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setFieldValue(name, "")}
                      size="small"
                      sx={{
                        marginRight: "5px",
                        fontSize: "10px",
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                </>
              )}
            </>
          ),
        }}
      >
        {mappedOptions}
      </TextField>
    </FormControl>
  );
};

export default SelectField;
